import { useCallback } from 'react'

// MFA token storage keys
const MFA_STORAGE_KEY = 'siteassist_mfa_token'
const MFA_EXPIRY_KEY = 'siteassist_mfa_expiry'
const MFA_EMAIL_KEY = 'siteassist_mfa_email'

interface MFATokenStorage {
  token: string
}

interface MFATokenOperations {
  getMFAToken: () => string | null
  storeMFAToken: (token: string) => void
  clearMFAToken: () => void
  getMFAExpiry: () => string | null
  storeMFAExpiry: (expiresAt: string) => void
  getMFAEmail: () => string | null
  storeMFAEmail: (email: string) => void
  clearMFAEmail: () => void
  isSessionValid: () => boolean
  clearMFASession: () => void
}

/**
 * Custom hook for MFA token management
 * Centralizes all token storage, retrieval, and cleanup operations
 */
export const useMFAToken = (): MFATokenOperations => {
  const getMFAToken = useCallback((): string | null => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return null
      }

      const stored = localStorage.getItem(MFA_STORAGE_KEY)
      if (!stored) return null

      const tokenData: MFATokenStorage = JSON.parse(stored)
      return tokenData.token
    } catch {
      return null
    }
  }, [])

  const storeMFAToken = useCallback((token: string): void => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return
      }

      const tokenData: MFATokenStorage = {
        token,
      }
      localStorage.setItem(MFA_STORAGE_KEY, JSON.stringify(tokenData))
    } catch (error) {
      console.error('Failed to store MFA token:', error)
    }
  }, [])

  const clearMFAToken = useCallback((): void => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return
      }

      localStorage.removeItem(MFA_STORAGE_KEY)
    } catch (error) {
      console.error('Failed to clear MFA token:', error)
    }
  }, [])

  const getMFAExpiry = useCallback((): string | null => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return null
      }

      return localStorage.getItem(MFA_EXPIRY_KEY)
    } catch {
      return null
    }
  }, [])

  const storeMFAExpiry = useCallback((expiresAt: string): void => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return
      }

      localStorage.setItem(MFA_EXPIRY_KEY, expiresAt)
    } catch (error) {
      console.error('Failed to store MFA expiry:', error)
    }
  }, [])

  const getMFAEmail = useCallback((): string | null => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return null
      }

      return localStorage.getItem(MFA_EMAIL_KEY)
    } catch {
      return null
    }
  }, [])

  const storeMFAEmail = useCallback((email: string): void => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return
      }

      localStorage.setItem(MFA_EMAIL_KEY, email)
    } catch (error) {
      console.error('Failed to store MFA email:', error)
    }
  }, [])

  const clearMFAEmail = useCallback((): void => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return
      }

      localStorage.removeItem(MFA_EMAIL_KEY)
    } catch (error) {
      console.error('Failed to clear MFA email:', error)
    }
  }, [])

  const isSessionValid = useCallback((): boolean => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return false
      }

      const token = getMFAToken()
      const expiry = getMFAExpiry()

      if (!token || !expiry) {
        return false
      }

      // UTC comparison using timestamps
      const nowUTC = Date.now() // Already in UTC milliseconds
      const expiryUTC = new Date(expiry).getTime() // Parse ISO string and get UTC milliseconds

      return nowUTC < expiryUTC
    } catch {
      return false
    }
  }, [getMFAToken, getMFAExpiry])

  const clearMFASession = useCallback((): void => {
    try {
      // Only operate in browser environment
      if (typeof window === 'undefined') {
        return
      }

      localStorage.removeItem(MFA_STORAGE_KEY)
      localStorage.removeItem(MFA_EXPIRY_KEY)
      localStorage.removeItem(MFA_EMAIL_KEY)
    } catch (error) {
      console.error('Failed to clear MFA session:', error)
    }
  }, [])

  return {
    getMFAToken,
    storeMFAToken,
    clearMFAToken,
    getMFAExpiry,
    storeMFAExpiry,
    getMFAEmail,
    storeMFAEmail,
    clearMFAEmail,
    isSessionValid,
    clearMFASession,
  }
}

// Export standalone utility functions for use in non-React contexts (like interceptors)
export const getMFATokenStandalone = (): string | null => {
  try {
    if (typeof window === 'undefined') {
      return null
    }

    const stored = localStorage.getItem(MFA_STORAGE_KEY)
    if (!stored) return null

    const tokenData: MFATokenStorage = JSON.parse(stored)
    return tokenData.token
  } catch {
    return null
  }
}

export const clearMFATokenStandalone = (): void => {
  try {
    if (typeof window === 'undefined') {
      return
    }

    localStorage.removeItem(MFA_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear MFA token:', error)
  }
}

// Export the interface for type safety
export type { MFATokenStorage, MFATokenOperations }
